import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { Sparkles, X, Maximize2, AlertTriangle, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { IconTip } from "@/components/ui/icon-tip";
import { BotonFlotanteIA } from "@/components/admin/BotonFlotanteIA";
import { ChatIaPanel } from "@/components/admin/ChatIaPanel";
import { useChatIaPanel } from "@/hooks/useChatIaPanel";
import { useAltaDireccionFilters } from "@/contexts/AltaDireccionFiltersContext";
import { useAgenteCeoDisponible } from "@/hooks/usePortalAltaDireccion/useAgenteCeo";
import { useChatAgenteCeo, type ContextoPantalla } from "@/hooks/usePortalAltaDireccion/useChatAgenteCeo";
import { AgenteCeoConversacion, HistorialAgenteCeo } from "./AgenteCeoConversacion";
import { useAtajoAgenteCeo } from "@/hooks/useAtajoAgenteCeo";
import { combinacionAgenteCeo, esMac, etiquetaCombinacion, registrarDuenoAtajo } from "@/lib/atajosTeclado";

/**
 * Punto de entrada del Agente CEO en todo el Portal Alta Dirección (spec 6.1).
 *
 * Va montado en el layout, así que está en todas las pantallas y recibe la ruta
 * y los filtros vigentes: preguntar "¿por qué subió?" sin decir de qué pantalla
 * se habla obligaría a repetir el contexto en cada mensaje.
 *
 * Usa el chat global del admin (`ChatIaPanel` + `ChatIaHistorial`): mismo botón flotante,
 * panel, historial y UX que la Guía del portal y el Analista de tickets.
 */

const RUTA_PAGINA = "/admin/portal-alta-direccion/agente-ceo";

/** Título legible de la pantalla actual, para el chip y para el prompt. */
function tituloDeRuta(ruta: string): string {
  const hoja = ruta.split("/").filter(Boolean).pop() ?? "";
  if (!hoja || hoja === "portal-alta-direccion") return "Portal Alta Dirección";
  const texto = hoja.split("-").join(" ");
  return texto.charAt(0).toUpperCase() + texto.slice(1);
}

export function AgenteCeoDrawer() {
  const location = useLocation();
  const { filters } = useAltaDireccionFilters();
  const { data: disponibilidad, isLoading } = useAgenteCeoDisponible();
  const panel = useChatIaPanel();
  const [conContexto, setConContexto] = useState(true);
  const chat = useChatAgenteCeo();

  // En su propia página el drawer sobra: sería la misma conversación dos veces.
  const enLaPagina = location.pathname.startsWith(RUTA_PAGINA);

  // El atajo lo resuelve un solo listener global (BuscadorMenus + lib/atajosTeclado):
  // con el atajo del agente aún en Ctrl/⌘+K sale un selector agente/buscador; ya
  // movido, Alt+K (⌥K en Mac) abre y cierra el agente. Aquí solo se registra la acción.
  const atajo = useAtajoAgenteCeo();
  const etiquetaAtajo = etiquetaCombinacion(combinacionAgenteCeo(atajo), esMac());
  const { alternar } = panel;
  useEffect(() => {
    if (enLaPagina) return;
    return registrarDuenoAtajo("agenteCeo", alternar);
  }, [enLaPagina, alternar]);

  if (isLoading || enLaPagina) return null;

  const filtrosVigentes = Object.fromEntries(
    Object.entries(filters).filter(([, v]) => v !== null && v !== ""),
  );
  const contextoPantalla: ContextoPantalla | null = conContexto
    ? {
        ruta: location.pathname,
        titulo: tituloDeRuta(location.pathname),
        filtros: Object.keys(filtrosVigentes).length ? filtrosVigentes : undefined,
      }
    : null;

  const resumenChip = [
    tituloDeRuta(location.pathname),
    filters.projectId ? `Proyecto ${filters.projectId}` : null,
    filters.period,
  ].filter(Boolean).join(" · ");

  const listo = disponibilidad === "listo";

  return (
    <>
      <BotonFlotanteIA
        icono={Sparkles}
        texto="Agente CEO"
        tooltip={`Agente CEO (${etiquetaAtajo})`}
        ariaLabel="Abrir Agente CEO"
        atajo={combinacionAgenteCeo(atajo) === "alt+k" ? "Alt+K" : "Control+K Meta+K"}
        onClick={() => panel.setAbierto(true)}
        abierto={panel.abierto}
        orden={2}
      />

      <ChatIaPanel
        abierto={panel.abierto}
        onAbiertoChange={panel.setAbierto}
        titulo="Agente CEO"
        descripcion="Servicio, crecimiento y qué construir"
        historialAbierto={panel.historialAbierto}
        onAlternarHistorial={panel.alternarHistorial}
        onNueva={() => {
          chat.nueva();
          panel.volverAlChat();
        }}
        nuevaDeshabilitada={!chat.hayConversacion}
        historial={
          listo ? (
            <HistorialAgenteCeo chat={chat} contextoPantalla={contextoPantalla} onElegido={panel.volverAlChat} />
          ) : undefined
        }
        accionesEncabezado={
          <IconTip label="Abrir en pantalla completa">
            <Button asChild size="icon" variant="ghost" className="size-8 text-muted-foreground">
              <Link to={RUTA_PAGINA} aria-label="Abrir en pantalla completa">
                <Maximize2 />
              </Link>
            </Button>
          </IconTip>
        }
        subencabezado={
          conContexto && listo ? (
            <div className="border-b border-border px-4 py-2">
              <span className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-muted px-2.5 py-1 text-[11px] text-muted-foreground">
                <span className="truncate">Viendo: {resumenChip}</span>
                <button
                  type="button"
                  onClick={() => setConContexto(false)}
                  aria-label="Preguntar sin el contexto de esta pantalla"
                  title="Preguntar sin el contexto de esta pantalla"
                  className="shrink-0 rounded hover:text-foreground"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            </div>
          ) : undefined
        }
      >
        {listo ? (
          // Abrir el panel NO llama al modelo ni crea conversación: nace del primer mensaje.
          <AgenteCeoConversacion chat={chat} contextoPantalla={contextoPantalla} />
        ) : (
          <AvisoNoDisponible motivo={disponibilidad} />
        )}
      </ChatIaPanel>
    </>
  );
}

/** Un director no debe leer un código de error: se le dice qué pasa y a quién pedirle. */
export function AvisoNoDisponible({ motivo }: { motivo?: "sin_migracion" | "sin_permiso" | "listo" }) {
  if (motivo === "sin_permiso") {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-2 p-8 text-center">
        <Lock className="h-6 w-6 text-muted-foreground" />
        <p className="text-sm font-medium text-foreground">No tienes acceso al Agente CEO</p>
        <p className="max-w-xs text-xs text-muted-foreground">
          Pídeselo a un administrador del portal. Necesitas el permiso del submenú «Agente CEO».
        </p>
      </div>
    );
  }
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-2 p-8 text-center">
      <AlertTriangle className="h-6 w-6 text-amber-600" />
      <p className="text-sm font-medium text-foreground">Falta preparar la base de datos</p>
      <p className="max-w-xs text-xs text-muted-foreground">
        Hay que ejecutar <code>Ejecuciones_manuales/20260925_ia_agente_ceo_f1.md</code> en Supabase.
        Mientras tanto el resto del portal funciona igual.
      </p>
    </div>
  );
}
